import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import recordEvent from 'platform/monitoring/record-event';
import { ContactInfoFormAppConfigContext } from '../components/ContactInfoFormAppConfigContext';
import { API_ROUTES } from '../constants';
import {
  clearAddressValidationKey,
  createTransaction,
  openModal,
  updateSelectedAddress,
  updateValidationKeyAndSave,
} from '../actions';
import {
  ADDRESS_VALIDATION_SUBMIT_STRATEGIES,
  USER_ENTERED_ADDRESS_ID,
  getAddressValidationSubmitStrategy,
} from '../util/address-validation/getAddressValidationSubmitStrategy';

/**
 * The three event handlers from the AddressValidationView class. The decision
 * making lives in getAddressValidationSubmitStrategy; this hook performs it.
 *
 * PORT NOTE: the class called `this.props.successCallback()` unguarded, which
 * would have thrown had anything rendered it without the prop (it is optional
 * in propTypes). Called optionally here.
 */
export const useAddressValidationHandlers = ({
  addressFromUser,
  addressValidationType,
  analyticsSectionName,
  confirmedSuggestions,
  overrideValidationKey,
  selectedAddress,
  selectedAddressId,
  updateProfileChoice,
  userHasBadAddress,
  successCallback,
}) => {
  const dispatch = useDispatch();
  const { prefillPatternEnabled, updateContactInfoForFormApp, fieldName } =
    useContext(ContactInfoFormAppConfigContext) ?? {};

  const onChangeSelectedAddress = (address, selectedId) => {
    const selected =
      selectedId === USER_ENTERED_ADDRESS_ID
        ? address
        : confirmedSuggestions?.[parseInt(selectedId, 10)];

    dispatch(updateSelectedAddress(selected, selectedId));
  };

  const onEditClick = () => {
    // updateProfileChoice rides along so the radio on the address form comes
    // back set the way the user left it.
    dispatch(
      openModal(addressValidationType, {
        ...addressFromUser,
        updateProfileChoice,
      }),
    );
  };

  const onSubmit = async event => {
    event.preventDefault();

    const strategy = getAddressValidationSubmitStrategy({
      selectedAddress,
      selectedAddressId,
      overrideValidationKey,
      analyticsSectionName,
      prefillPatternEnabled,
      updateProfileChoice,
      userHasBadAddress,
    });

    if (strategy.type === ADDRESS_VALIDATION_SUBMIT_STRATEGIES.FORM_ONLY) {
      // The form app owns both the initial keys and any the profileContactInfo
      // factory customised, so the update goes back through the context.
      updateContactInfoForFormApp(
        fieldName,
        strategy.payload,
        updateProfileChoice,
      );

      // Navigates back to the ContactInfo page.
      successCallback?.();

      dispatch(openModal());
      return;
    }

    recordEvent(strategy.analyticsEvent);

    if (strategy.shouldClearValidationKey) {
      dispatch(clearAddressValidationKey());
    }

    const save =
      strategy.type ===
      ADDRESS_VALIDATION_SUBMIT_STRATEGIES.UPDATE_VALIDATION_KEY
        ? updateValidationKeyAndSave
        : createTransaction;

    await dispatch(
      save(
        API_ROUTES.ADDRESSES,
        strategy.method,
        addressValidationType,
        strategy.payload,
        analyticsSectionName,
      ),
    );
  };

  return { onChangeSelectedAddress, onEditClick, onSubmit };
};
